// Release B · B0-Closeout — Communications Settings (SendGrid section).
// Route: /admin/settings/communications
// Strict language: provider_accepted ≠ delivered. No API key preview surfaced.
import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
// Release C-1 — Microsoft 365 section (Entra OAuth + Approved Mailboxes).
import MicrosoftCommunications from "./MicrosoftCommunications";

const DISPLAY_CHIP = {
  connected:            { bg: "#DCFCE7", fg: "#166534", label: "Connected" },
  configured_disconnected: { bg: "#FEE2E2", fg: "#991B1B", label: "Configured but Disconnected" },
  authorization_required: { bg: "#FEF3C7", fg: "#92400E", label: "Authorization Required" },
  degraded:             { bg: "#FED7AA", fg: "#9A3412", label: "Degraded" },
  error:                { bg: "#FEE2E2", fg: "#991B1B", label: "Error" },
  not_configured:       { bg: "#F1F5F9", fg: "#334155", label: "Not Configured" },
};
function DisplayStateChip({ state }) {
  const c = DISPLAY_CHIP[state] || DISPLAY_CHIP.not_configured;
  return <span data-testid={`comms-display-state-${state}`} style={{
    display: "inline-block", padding: "3px 12px", borderRadius: 9999,
    background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600,
  }}>{c.label}</span>;
}

const DEL_CHIP = {
  queued: { bg: "#F1F5F9", fg: "#334155" },
  provider_accepted: { bg: "#FEF3C7", fg: "#92400E" },
  delivered: { bg: "#DCFCE7", fg: "#166534" },
  deferred: { bg: "#DBEAFE", fg: "#1E40AF" },
  bounced: { bg: "#FEE2E2", fg: "#991B1B" },
  blocked: { bg: "#FEE2E2", fg: "#991B1B" },
  dropped: { bg: "#FEE2E2", fg: "#991B1B" },
  failed: { bg: "#FEE2E2", fg: "#991B1B" },
};
function DeliveryChip({ state }) {
  const c = DEL_CHIP[state] || DEL_CHIP.queued;
  return <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600 }}>{state}</span>;
}

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

const RAW_STATE_CHIP_LABEL = {
  // Fallback labels for the safety net when `display_state` is malformed
  // or absent from the response. Backend is authoritative — the chip
  // renders directly from `display_state` when present.
  connected: "Connected",
  authorization_required: "Authorization Required",
  configured_disconnected: "Configured but Disconnected",
  degraded: "Degraded",
  error: "Error",
  not_configured: "Not Configured",
};

export default function CommunicationsSettings() {
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [sending, setSending] = useState(false);
  const [twilio, setTwilio] = useState(null);
  const location = useLocation();

  const load = async () => {
    try { const r = await api.get("/admin/communications/status"); setStatus(r.data); }
    catch { toast.error("Failed to load communications status"); }
  };
  const loadTwilio = async () => {
    try {
      const r = await api.get("/integrations/twilio");
      setTwilio(r.data);
    } catch {
      setTwilio({ __missing: true });
    }
  };
  useEffect(() => { load(); loadTwilio(); }, []);

  // Stage A · Twilio section scroll-focus on ?section=twilio or #twilio-sms.
  useEffect(() => {
    const wantTwilio = location.search.includes("section=twilio")
      || location.hash === "#twilio-sms";
    if (wantTwilio && twilio) {
      const el = document.getElementById("twilio-sms");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [twilio, location]);

  const runTestSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/communications/sendgrid/test-send", {});
      setResults(r.data);
      toast.success("Test-send queued — both messages provider_accepted, awaiting delivered event");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "test_send_rate_limited") toast.warning(`Rate-limited — wait ${d.retry_after_seconds}s`);
      else if (d?.code === "recipient_not_configured") toast.error(d.message);
      else toast.error(d?.message || "test-send failed");
    } finally { setSending(false); }
  };

  if (!status) return <AdminAppShell current="/admin/settings/communications"><div style={{ padding: 24 }}>Loading…</div></AdminAppShell>;

  const cred = status.credential || {};
  return (
    <AdminAppShell current="/admin/settings/communications">
      <div data-testid="comms-settings-page" style={{ padding: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Communications Settings</h1>
        <div style={{ color: "#475569", fontSize: 13, marginTop: 4, marginBottom: 20 }}>
          Honest per-tenant status for transactional email. No secret values are shown on this page — only whether a credential is present and its source.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 20 }}>
          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Provider</div>
              <div style={{ fontSize: 20, fontWeight: 700, margin: "4px 0 8px" }}>Transactional Email — SendGrid</div>
              <DisplayStateChip state={status.display_state} />
              <div style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
                Raw state: <code>{status.state}</code><br />
                Last check: {status.credential?.last_check_detail || "—"} at {fmt(status.credential?.last_check_at)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>API Key</div>
              <div data-testid="comms-cred-present" style={{ fontSize: 18, fontWeight: 700, margin: "4px 0" }}>
                {cred.api_key_present ? "Present" : "Missing"}
              </div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1, marginTop: -18, marginBottom: 4 }}>API Key Present</div>
              <div style={{ fontSize: 12, color: "#475569" }}>
                Source: <b>{cred.source || "—"}</b>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                No preview is exposed — even a masked prefix is a leakage surface.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Authenticated sender</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{status.from_email || "—"}</div>
              <div style={{ fontSize: 12, color: "#475569" }}>Reply-to: {status.reply_to || "—"}</div>
              <div style={{ fontSize: 12, color: "#475569" }}>Domain: {status.authenticated_domain || "—"}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Webhook</div>
              <div data-testid="comms-webhook-configured" style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                {status.webhook?.public_key_configured ? "Public key configured" : "Public key NOT configured"}
              </div>
              <div style={{ fontSize: 12, color: "#475569" }}>
                Last verified event: {fmt(status.webhook?.last_signature_verify_at)}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card style={{ marginBottom: 20 }}>
          <CardContent style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Owner test delivery</div>
                <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
                  Sends two production-equivalent messages (one per Owner mailbox). Rate-limited to 1 set per 60 seconds. Audited. <b>HTTP 202 is provider acceptance — not delivery.</b>
                </div>
              </div>
              <Button data-testid="comms-test-send" onClick={runTestSend} disabled={sending}>
                {sending ? "Sending…" : "Send Test Email"}
              </Button>
            </div>
            {results && (
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                {(results.results || []).map((r) => (
                  <div key={r.outbox_id} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 10 }}>
                    <div><b>{r.label}</b> → {r.recipient}</div>
                    <div>state: <DeliveryChip state={r.state} /></div>
                    <div style={{ marginTop: 4, color: "#475569" }}>msg_id: <code>{(r.provider_message_id || "—")}</code></div>
                    <div style={{ color: "#475569" }}>status_code: {r.provider_status_code} · attempts: {r.attempts}</div>
                    {r.send_error && <div style={{ color: "#991b1b", marginTop: 4 }}>error: {r.send_error}</div>}
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1", color: "#64748b", fontStyle: "italic", fontSize: 11 }}>
                  {results.note}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Last delivered</div>
              {status.last_delivered ? (
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  <div>{status.last_delivered.recipient || status.last_delivered.to}</div>
                  <div style={{ color: "#475569" }}>{status.last_delivered.subject}</div>
                  <div style={{ color: "#64748b", marginTop: 4 }}>at {fmt(status.last_delivered.last_provider_event_at)}</div>
                  <div style={{ color: "#64748b" }}>msg_id: <code>{(status.last_delivered.provider_message_id || "—")}</code></div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>
                  No delivered webhook received yet for this tenant. Provider-accepted messages may be awaiting the delivered event.
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Last failure</div>
              {status.last_failure ? (
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  <div>{status.last_failure.recipient || status.last_failure.to} · <DeliveryChip state={status.last_failure.state} /></div>
                  <div style={{ color: "#475569" }}>{status.last_failure.subject}</div>
                  <div style={{ color: "#991b1b", marginTop: 4 }}>reason: {status.last_failure.sanitized_error_reason || status.last_failure.send_error || "—"}</div>
                  <div style={{ color: "#64748b" }}>at {fmt(status.last_failure.updated_at)}</div>
                </div>
              ) : (<div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>No recent failures.</div>)}
            </CardContent>
          </Card>
        </div>

        {/* Release C-1 — Microsoft 365 section (Entra OAuth + Mailboxes) */}
        <MicrosoftCommunications />

        {/* Stage A · Business SMS — Twilio · info-only section, no send handler wired */}
        <Card id="twilio-sms" data-testid="comms-twilio-section" style={{ marginTop: 20 }}>
          <CardContent style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Business SMS</div>
                <div style={{ fontSize: 20, fontWeight: 700, margin: "4px 0 8px" }}>Business SMS — Twilio</div>
                {/* Chip renders authoritative `display_state` from the
                    singular /api/integrations/{key} endpoint (Stage A
                    backend parity fix — extracted shared helper). Safe
                    unknown-value fallback: if `display_state` is somehow
                    missing/malformed, render "Unknown" via the raw state
                    as-is rather than fabricating "Not Configured". */}
                {twilio && !twilio.__missing ? (
                  (twilio.display_state && DISPLAY_CHIP[twilio.display_state]) ? (
                    <DisplayStateChip state={twilio.display_state} />
                  ) : (
                    <span
                      data-testid="comms-display-state-unknown"
                      style={{
                        display: "inline-block", padding: "3px 12px",
                        borderRadius: 9999, background: "#F1F5F9",
                        color: "#334155", fontSize: 12, fontWeight: 600,
                      }}
                    >
                      {(twilio.display_state || twilio.state || "Unknown")
                        .toString()
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                  )
                ) : (
                  <DisplayStateChip state="not_configured" />
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
                  Provider chip reflects authoritative server state on every fresh load.
                </div>
              </div>
              <Link to="/admin/integrations" data-testid="comms-twilio-open-integration-card">
                <Button size="sm" variant="outline">Open Twilio integration card →</Button>
              </Link>
            </div>

            {twilio && !twilio.__missing && (
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, fontSize: 12 }}>
                <div style={{ padding: 10, border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Account SID</div>
                  <div data-testid="comms-twilio-masked-account-sid" style={{ fontFamily: "monospace", marginTop: 4 }}>
                    {twilio?.masked_preview?.account_sid || "—"}
                  </div>
                </div>
                <div style={{ padding: 10, border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Auth Token</div>
                  <div data-testid="comms-twilio-auth-token-status" style={{ fontFamily: "monospace", marginTop: 4 }}>
                    {twilio?.masked_preview?.auth_token
                      ? <span>{twilio.masked_preview.auth_token} · <b>Stored securely</b></span>
                      : "Not stored"}
                  </div>
                </div>
                <div style={{ padding: 10, border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Messaging Service SID</div>
                  <div data-testid="comms-twilio-masked-msid" style={{ fontFamily: "monospace", marginTop: 4 }}>
                    {twilio?.masked_preview?.messaging_service_sid || "— missing —"}
                  </div>
                </div>
                <div style={{ padding: 10, border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 }}>Phone Number</div>
                  <div data-testid="comms-twilio-masked-phone" style={{ fontFamily: "monospace", marginTop: 4 }}>
                    {twilio?.masked_preview?.phone_number || "— missing —"}
                  </div>
                </div>
              </div>
            )}

            {twilio?.last_check_detail && (
              <div style={{ marginTop: 10, padding: 10, background: "#FEF3C7", borderRadius: 6, fontSize: 12, color: "#78350F" }}>
                <b>Last verification:</b> {twilio.last_check_detail} · {fmt(twilio.last_check_at)}
              </div>
            )}

            <div style={{ marginTop: 12, padding: 10, background: "#F1F5F9", borderRadius: 6, fontSize: 11, color: "#475569" }}>
              <b>Stage A — SMS send path deferred.</b> ConstructionOS will not send any SMS until the
              CRM consent + opt-out foundation is verified. The button below is deliberately disabled;
              no handler is wired for outbound messages in this stage. STOP/HELP autoresponders
              are drafted in <code>A2P_STOP_HELP_RESPONSES.md</code> and will bind after Twilio A2P
              approval + Owner authorization to proceed to Stage B.
            </div>

            <div style={{ marginTop: 8 }}>
              <Button
                size="sm"
                disabled
                data-testid="comms-twilio-send-test-disabled"
                title="Awaiting CRM consent and opt-out foundation"
                aria-disabled="true"
                style={{ opacity: 0.55, cursor: "not-allowed" }}
              >
                Send Controlled Test SMS · Awaiting CRM consent and opt-out foundation
              </Button>
            </div>
          </CardContent>
        </Card>

        {(status.suppression_warnings || []).length > 0 && (
          <Card style={{ marginTop: 12, borderColor: "#fca5a5" }}>
            <CardContent style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b" }}>Suppression warnings</div>
              <ul style={{ margin: "6px 0 0", padding: "0 0 0 18px", fontSize: 12, color: "#475569" }}>
                {status.suppression_warnings.map((s, i) => (
                  <li key={i}>{s.address} — {s.state} at {fmt(s.updated_at)} — {s.send_error}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminAppShell>
  );
}
